<template>
  <PortalLayout section="Customization">
    <div class="mt-4">
      <Loading v-if="isPropertyLoading || isLoading" />
      <div v-else>
        <div id="custom-content">
          <select
            v-model="selectedContentType"
            class="block w-full px-4 p-3 pr-8 mt-1 bg-gray-100 border border-x-gray-300 rounded"
            @change="contentTypeChanged($event.target as HTMLSelectElement)"
          >
            <option :value="ContentType.None">--Select Customization to Edit--</option>
            <option :value="ContentType.Logo">Custom Logo</option>
            <option :value="ContentType.HomePageHeader">Home Page Header</option>
            <option :value="ContentType.TrainingDescription">Training description</option>
            <option :value="ContentType.RespondHeader">Respond Header</option>
            <option :value="ContentType.ExpiredTrainingEmailSubject">Expired Training Email Subject</option>
            <option :value="ContentType.ExpiredTrainingEmailBody">Expired Training Email Body</option>
          </select>

          <div
            class="edit-panel-container mt-1 border p-4"
            v-if="selectedContentType !== ContentType.None"
          >
            <div v-if="error" class="text-red-500">{{ error }}</div>
            <div v-if="saveSuccessful" class="text-green-500">
              Content saved successfully
            </div>

            <!-- Custom Logo -->
            <div class="custom-logo" v-if="selectedContentType === ContentType.Logo">
              <div class="border p-4 mb-4">
                <h1 class="text-xl font-bold">Custom Logo</h1>
                <div v-if="customLogo" class="bg-gray-100 border-t my-2">
                  <!-- <img
              :src="`${contentApiPath}/${property.siteCode}/${customLogo.contentId}`"
              class="h-48 px-4 py-4"
            /> -->
                  <div
                    class="px-4 py-2 cursor-pointer hover:bg-blue-200"
                    @click="editClick(customLogo)"
                  >
                    <div>{{ customLogo.contentValue }}</div>
                    <div class="ml-2 font-mono text-sm text-gray-700">
                      {{ customLogo.mimeType }}
                    </div>
                  </div>
                  <div class="p-2">
                    <input
                      class="font-mono p-1 px-2 w-full"
                      :value="`${contentApiPath}/${property.siteCode}/${customLogo.contentId}`"
                      readonly
                    />
                  </div>
                </div>
                <AddButton v-else class="mt-2" @click="addClick(1)"
                  >Add Custom Logo</AddButton
                >
              </div>
            </div>

            <!-- Home Page Header -->
            <div
              class="home-page-header"
              v-if="selectedContentType === ContentType.HomePageHeader"
            >
              <h1 class="text-lg font-bold pt-4">Home Page Header</h1>
              <h3 class="ml-2 mb-4 text-sm font-serif">
                Default Value: "Welcome to the Building"
              </h3>
              <!-- <img
              class="border border-gray-200 m-8 shadow-lg"
              src="/images/Custom_Response_header_screenshot.png"
              alt=""
            /> -->
              <BaseInput label="Custom Header Text:" v-model="contentValue" />
            </div>

            <!-- Respond Header -->
            <div
              class="respond-edit-panel"
              v-if="selectedContentType === ContentType.RespondHeader"
            >
              <h1 class="text-lg font-bold pt-4">Home Page Respond Section Header</h1>
              <h3 class="ml-2 mb-4 text-sm font-serif">
                Default Value: "Response Steps"
              </h3>
              <img
                class="border border-gray-200 m-8 shadow-lg"
                src="/images/Custom_Response_header_screenshot.png"
                alt=""
              />
              <BaseInput label="Custom Header Text:" v-model="contentValue" />
            </div>

            <!-- Training Description -->
            <div
              class="training-description"
              v-if="selectedContentType === ContentType.TrainingDescription"
            >
              <h1 class="text-lg font-bold pt-4">Training Description</h1>
              <h3 class="ml-2 mb-4 text-sm font-serif">
                Default Value: "Please complete the following training"
              </h3>
              <!-- <img
              class="border border-gray-200 m-8 shadow-lg"
              src="/images/Custom_Response_header_screenshot.png"
              alt=""
            /> -->
              <BaseInput label="Custom Header Text:" v-model="contentValue" />
            </div>

            <!-- Expired Training Email Subject -->
            <div
              class="expired-training-email-subject"
              v-if="selectedContentType === ContentType.ExpiredTrainingEmailSubject"
            >
              <h1 class="text-lg font-bold pt-4">Expired Training Email Subject</h1>
              <h3 class="ml-2 mb-4 text-sm font-serif">
                Default Value: "Your Training Has Expired"
              </h3>
              <BaseInput label="Email Subject:" v-model="contentValue" />
            </div>

            <!-- Expired Training Email Body -->
            <div
              class="expired-training-email-body"
              v-if="selectedContentType === ContentType.ExpiredTrainingEmailBody"
            >
              <h1 class="text-lg font-bold pt-4">Expired Training Email Body</h1>
              <h3 class="ml-2 mb-4 text-sm font-serif">
                Default Value: System default expired training email body
              </h3>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email Body:</label>
              <div v-if="Ckeditor && Editor" class="ckeditor-text">
                <component :is="Ckeditor" :editor="Editor" v-model="contentValue" />
              </div>
              <div class="mt-3 p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
                <div class="font-semibold mb-1">Available template variables:</div>
                <div class="font-mono grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                  <span>[[firstname]]</span><span>[[lastname]]</span>
                  <span>[[email]]</span><span>[[propertyname]]</span>
                  <span>[[sitecode]]</span><span>[[clientname]]</span>
                  <span>[[tenantname]]</span><span>[[mostrecenttraining]]</span>
                  <span>[[expireaftermonths]]</span><span>[[monthssincelasttraining]]</span>
                  <span>[[personid]]</span><span>[[portalid]]</span>
                </div>
              </div>
            </div>

            <!-- Display buttons if not editing logo, since logo editing includes file upload which has its own save/reset flow -->
            <div class="flex mt-8" v-if="selectedContentType !== ContentType.Logo">
              <button
                type="button"
                class="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-red-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                @click="resetContent"
              >
                Reset to Default
              </button>
              <span class="flex-grow"></span>
              <button
                type="button"
                class="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                @click="saveContent"
              >
                Save
              </button>
            </div>
          </div>
        </div>
        <div class="border p-4 mb-4 mt-8">
          <h1 class="text-xl font-bold">File Upload</h1>
          <h3 class="ml-2 mb-4 mt-6 text-sm font-serif">
            Upload additional content files to this portal that can be embedded 
              into Respond or Alert content
          </h3>
          <AddButton class="mt-2" @click="addClick(0)">Upload File</AddButton>
          <div
            v-for="content in additionalContent"
            :key="content.contentId"
            class="bg-gray-100 border-t my-2"
          >
            <div
              class="px-4 py-2 cursor-pointer hover:bg-blue-200"
              @click="editClick(content)"
            >
              <div>{{ content.contentValue }}</div>
              <div class="ml-2 font-mono text-sm text-gray-700">
                {{ content.mimeType }}
              </div>
            </div>
            <div class="p-2 flex flex-1 display-flex">
              <input
                class="font-mono p-1 px-2 w-full"
                :value="`${contentApiPath}/${property.siteCode}/${content.contentId}`"
                readonly
              />
              <a
                :href="`${contentApiPath}/${property.siteCode}/${content.contentId}`"
                class="text-blue-600 underline"
                target="_blank"
                >View</a>
            </div>
          </div>
          <AddButton v-if="additionalContent?.length > 5" @click="addClick(0)">
            Upload File
          </AddButton>
        </div>
      </div>
      <EditPopup
        entity-name="Content"
        :is-visible="isPopupVisible"
        :is-edit-mode="isEditing"
        :save-error="saveError"
        :delete-enabled="true"
        @close="isPopupVisible = false"
        @save="saveClick"
        @delete="deleteClick"
      >
        <div class="mb-4">
          Content Type:
          <div>
            {{ ContentType[contentSelectedType] }}
          </div>
        </div>
        <BaseInput
          label="File Name"
          v-model="contentFileName"
          :error="errors.contentFileName"
        />
        <input type="file" class="pt-4" @change="fileChange" />
      </EditPopup>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { useCKEditor } from "../../composables/useCKEditor";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import {
  ContentDTO,
  ContentType,
  PortalContentDTO,
  SaveContentRequest,
} from "../../store/generatedFetchClient";

const contentApiPath = "/api/property/content/file";
const { Ckeditor, Editor } = useCKEditor();
const bldgStore = useBldgStore();
var isPropertyLoading = computed(() => bldgStore.isLoading);
var isLoading = ref(false);
var property = computed(() => bldgStore.property);
var selectedContentType = ref(ContentType.RespondHeader);
var contentValue = ref("");
var error = ref("");
var saveSuccessful = ref(false);

onMounted(loadData);
watch(property, loadData);

async function loadData() {
  isLoading.value = true;
  if (property.value?.portalId) {
    await bldgStore.tryGetBuildingData(bldgStore.siteCode);
    if (property.value?.content && selectedContentType?.value) {
      contentValue.value = getContent(selectedContentType.value)?.contentValue;
    }
  }
  isLoading.value = false;
}

// Popup stuff
let isPopupVisible = ref(false);
let isEditing = ref(false);
let saveError = ref("");

var uploadedContent = computed(() => {
  if (property.value.portalId)
    return property.value.content?.filter((c) => c.isStoredExternally);
  return [] as PortalContentDTO[];
});
var additionalContent = computed(() => {
  if (property.value.portalId)
    return uploadedContent.value?.filter((c) => c.contentType == ContentType.None);
  return [] as PortalContentDTO[];
});
var customLogo = computed(() => {
  return uploadedContent.value?.find((c) => c.contentType == ContentType.Logo);
});

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    contentFileName: yup.string().required().max(255).label("File Name"),
  }),
  initialValues: {
    contentFileName: "",
  },
});

let contentId = ref(0);
const { value: contentFileName } = useField<string>("contentFileName");
let file = ref<File>();
let contentMimeType = "";
let contentSelectedType = ref(0);
let contentOldFileName = "";

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  console.log("Saving content");
  if (!property.value) {
    return;
  }

  if (!isEditing.value && !file.value) {
    saveError.value = "You must specify a file to upload.";
    return;
  }

  // Check for invalid chars
  const invalidChars = '\\/:*?"<>|';
  const validFilename = [...invalidChars].every(
    (c) => contentFileName.value.indexOf(c) < 0
  );
  if (!validFilename) {
    saveError.value = `Filename cannot include any of the following characters: ${invalidChars}`;
    return;
  }

  for (let content of uploadedContent.value) {
    if (
      contentId.value != content.contentId &&
      content.contentValue.normalize() === contentFileName.value.normalize()
    ) {
      saveError.value = "Filename already exists.";
      return;
    }
  }

  let saveContent: SaveContentRequest = {
    contentId: contentId.value,
    portalId: property.value?.portalId,
    contentType: contentSelectedType.value,
    isStoredExternally: true,
    contentValue: contentFileName.value,
    mimeType: file.value?.type ?? contentMimeType,
  };

  saveError.value = "";

  console.log("Saving content", saveContent);
  if (property.value && property.value.portalId) {
    saveError.value = await bldgStore.saveContent(saveContent); // Save content to DB
    if (!saveError.value) {
      if (file.value) {
        // Upload new content and remove old file if necessary
        let fileParameter = { data: file.value, fileName: contentFileName.value };
        saveError.value = await bldgStore.uploadContent(
          fileParameter,
          property.value.siteCode,
          contentOldFileName
        );
      } else if (contentOldFileName.normalize() !== contentFileName.value.normalize()) {
        // Rename doc in-place
        saveError.value = await bldgStore.renameUploadedContent(
          property.value.siteCode,
          contentOldFileName,
          contentFileName.value
        );
      }
      if (!saveError.value) {
        isPopupVisible.value = false;
        file.value = undefined;

        isLoading.value = true;
        await bldgStore.tryGetBuildingData(bldgStore.siteCode, true);
        isLoading.value = false;
      }
    }
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("Error trying to submit form", ctx);
}

let addClick = function (type?: number) {
  contentId.value = 0;
  contentFileName.value = "";
  contentOldFileName = undefined;
  contentMimeType = "";
  file.value = undefined;
  contentSelectedType.value = type;

  saveError.value = "";
  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selected: ContentDTO) {
  contentId.value = selected.contentId ?? 0;
  contentFileName.value = selected.contentValue ?? "";
  contentOldFileName = contentFileName.value;
  contentMimeType = selected.mimeType;
  contentSelectedType.value = selected.contentType;
  file.value = undefined;

  saveError.value = "";
  isEditing.value = true;
  isPopupVisible.value = true;
};

async function deleteClick() {
  if (isEditing.value && contentId.value > 0) {
    saveError.value = await bldgStore.deleteContent(
      contentId.value,
      property.value.siteCode
    );

    if (!saveError.value) {
      isPopupVisible.value = false;

      isLoading.value = true;
      await bldgStore.tryGetBuildingData(bldgStore.siteCode, true);
      isLoading.value = false;
    }
  }
}

function fileChange(event: Event) {
  let filepath = (event.target as HTMLInputElement).value;
  file.value = (document.querySelector("[type=file]") as HTMLInputElement).files[0];
  contentFileName.value = filepath.substring(filepath.lastIndexOf("\\") + 1);
}

function contentTypeChanged(target: HTMLSelectElement) {
  saveSuccessful.value = false;
  selectedContentType.value = parseInt(target.value);
  contentValue.value = getContent(selectedContentType.value)?.contentValue;
}
function getContent(contentType: ContentType) {
  if (property?.value?.content) {
    return property.value.content.find((c) => c.contentType === contentType);
  }
  return null;
}

async function saveContent() {
  error.value = "";
  saveSuccessful.value = false;
  console.log(`saving content ${selectedContentType.value}: ${contentValue.value}`);
  var content = getContent(selectedContentType.value);
  if (!content) {
    content = {
      contentType: selectedContentType.value,
      isStoredExternally: false,
      mimeType: "text/plain",
    } as PortalContentDTO;
  }
  content.contentValue = contentValue.value;
  Object.assign(content, { portalId: property.value.portalId });

  var saveResult = await bldgStore.saveContent(content);
  if (!saveResult) {
    console.log("content saved");
    saveSuccessful.value = true;
  } else {
    console.error("content save failed with message: ", saveResult);
    error.value = saveResult;
  }
}
async function resetContent() {
  console.log("reset content");
  var content = getContent(selectedContentType.value);
  var saveResult = await bldgStore.deleteContent(content.contentId);
  if (!saveResult) {
    console.log("content reset");
    saveSuccessful.value = true;
    contentValue.value = "";
  } else {
    console.error("content reset failed with message: ", saveResult);
    error.value = saveResult;
  }
}
</script>
